
<template>
  <div class="base-list-page">
    <el-row class="detail-header-wrap">
      <BaseHeader
          :title="title"
          :class="titleClass"
          :icon="icon" />
      <slot name="message" />
      <slot name="headerAction" />
      <slot name="headerActionMode" />
    </el-row>
    <BaseSearchConditionModal
        v-if="showSearchComponent"
        ref="searchComponent"
        :function-key="functionKey"
        :no-condition="noCondition"
        :no-search="noSearch"
        :keyword="keyword"
        :is-and-search="isAndSearch"
        :conditions="conditions"
        :base-query="baseQuery"
        :store-path="storePath"
        :res-path="responsePath"
        :save-key="saveKey"
        :is-max-get-count="isMaxGetCount"
        :max-get-count="maxGetCount"
        :is-search-condition-switch="isSearchConditionSwitch"
        :name="name"
        @dataConvert="dataConvert"
        @maxGetCount="maxGetCount"
        @search="onSearch"
        @searched="onSearched" />
    <template
        v-else-if="showCustomSearchComponent">
      <slot name="customSearchComponent" />
    </template>
    <slot name="search" />
    <slot name="prefixTable" />

    <SalesSumTableList
        :show-selection="showSelection"
        :store-path="storePath"
        :span-method="spanMethod"
        :can-select-method="canSelectMethod"
        :row-key="rowKey"
        :min-column-width="minColumnWidth"
        :async-render="asyncRender"
        :editable="editable"
        :row-class-name="rowClassName"
        :cell-style="cellStyle"
        :table-data="sumTableList"
        :display-data="sumDisplayList" />

    <BaseTableList
        ref="tableList"
        :class="tableClass"
        :table-set-list="tableSetList"
        :display-items="displayItems"
        :default-sort="sortCondition"
        :show-selection="showSelection"
        :store-path="storePath"
        :span-method="spanMethod"
        :can-select-method="canSelectMethod"
        :row-key="rowKey"
        :min-column-width="minColumnWidth"
        :async-render="asyncRender"
        :editable="editable"
        :row-class-name="rowClassName"
        :cell-style="cellStyle"
        @row-click="onRowClick"
        @sort-change="onSortChange">
      <template
          slot="operation"
          slot-scope="{ row }">
        <slot
            name="tableOperation"
            :row="row" />
      </template>
    </BaseTableList>
    <BasePagination
        v-if="showPagination"
        :current-page="_currentPage"
        :size="pageSize"
        :total="totalSize"
        :total-count="totalCount"
        @size-change="onPaginationSizeChange"
        @current-change="onPaginationCurrentChange" />
    <slot name="dialog" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import _ from 'lodash'
import common from '@/utils/common'
import message from '@/utils/message'

import BaseHeader from '@/components/molecules/BaseHeader'
import BaseSearchConditionModal from '@/components/pages/report/sales/SalesBaseSearchConditionModal'
import BaseTableList from '@/components/molecules/BaseTableList'
import BasePagination from '@/components/molecules/BasePagination'
import SalesSumTableList from '@/components/pages/report/sales/SalesSumTableList'

export default {
  name: 'BaseListPage',
  components: {
    BaseHeader,
    BaseSearchConditionModal,
    SalesSumTableList,
    BaseTableList,
    BasePagination
  },
  props: {
    functionKey: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    // 検索未対応コンポーネント用（暫定）
    noCondition: {
      type: Boolean
    },
    noSearch: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    titleClass: {
      type: [String, Object, Array],
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    tableClass: {
      type: [String, Object, Array],
      default: ''
    },
    showSelection: {
      type: Boolean,
      default: false
    },
    baseQuery: {
      type: String,
      default: ''
    },
    storePath: {
      type: String,
      default: ''
    },
    responsePath: {
      type: String,
      default: ''
    },
    saveKey: {
      type: String,
      default: ''
    },
    tableSetList: {
      type: Array,
      default: () => { return [] }
    },
    tableDataList: {
      type: Array,
      default: () => { return [] }
    },
    showSearchComponent: {
      type: Boolean,
      default: true
    },
    showCustomSearchComponent: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    currentPage: {
      type: Number,
      default: undefined
    },
    currentPageKey: {
      type: String,
      default: 'currentPage'
    },
    sizeKey: {
      type: String,
      default: 'size'
    },
    saveCurrentPage: {
      type: Boolean,
      default: true
    },
    isParentLoaded: {
      type: Boolean,
      default: true
    },
    spanMethod: {
      type: Function,
      default: null
    },
    canSelectMethod: {
      type: Function,
      default: null
    },
    rowKey: {
      type: String,
      default: ''
    },
    minColumnWidth: {
      type: [String, Number],
      default: null
    },
    isLimitCheck: {
      type: Boolean,
      default: true
    },
    // 取得件数の選択が可能であるかどうか
    isMaxGetCount: {
      type: Boolean,
      required: false,
      default: false
    },
    asyncRender: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Object,
      default: () => {
        return {}
      }
    },
    rowClassName: {
      type: [ String, Function ],
      default: ''
    },
    cellStyle: {
      type: Function,
      default: () => {}
    },
    sumTableList: {
      type: Array,
      default: () => { return [] }
    },
    sumDisplayList: {
      type: Array,
      default: () => { return [] }
    }
  },
  data () {
    return {
      localCurrentPage: 0
    }
  },
  computed: {
    ...mapGetters('LocalStorage', {
      getSortCondition: 'sortCondition',
      getPaginationSize: 'paginationSize'
    }),
    store () {
      return this.$store.state[this.storePath] || {}
    },
    keyword () {
      return this.store.searchKeyword || ''
    },
    isAndSearch () {
      return this.store.isAndSearch || false
    },
    conditions () {
      return this.store.searchConditions || {}
    },
    displayItems () {
      if (this.showPagination) {
        const head = this._currentPage * this.pageSize
        return this.tableDataList.slice(head, head + this.pageSize)
      } else {
        return this.tableDataList
      }
    },
    sortCondition () {
      return this.getSortCondition(this.name)
    },
    _currentPage () {
      if (this._saveCurrentPage) {
        return this.showPagination
          ? this.store[this.currentPageKey] || 0
          : 0
      } else {
        return this.currentPage != null ? this.currentPage : this.localCurrentPage || 0
      }
    },
    _saveCurrentPage () {
      return this.saveCurrentPage && this.currentPage == null
    },
    totalSize () {
      return this.tableDataList.length
    },
    totalCount () {
      return (this.$store.state[this.storePath] || {}).totalCount || 0
    },
    pageSize () {
      return _.get(this.$store.state, `${this.storePath}.${this.sizeKey}`, 10)
    },
    maxGetCount () {
      return this.store.maxGetCount || 1000
    },
    isSearchConditionSwitch () {
      // 商品別、エール別のみ検索条件の表示切替を有効にする
      if (this.name === 'SalesProductPhysicalList' || this.name === 'SalesProductDigitalList' || this.name === 'SalesYellDigitalList') {
        return true
      } else {
        return false
      }
    }
  },
  watch: {
    isParentLoaded: {
      handler: function (val, oldVal) {
        // 親側でマスタ情報の取得完了後、保存されている検索条件を設定
        if (this.isParentLoaded) {
          common.recoverSearchConditions(this.saveKey, this.conditions, `${this.storePath}/searchKeyword`, `${this.storePath}/isAndSearch`, `${this.storePath}/maxGetCount`)
        }
      }
    }
  },
  created () {
    // ローカルストレージにサイズ情報がある場合は読み取りストアに保存。ない場合は10。
    this.setStore(`${this.storePath}/${this.sizeKey}`, this.getPaginationSize(this.name) || 10)

    if (this.showSearchComponent) {
      // 保存されている検索条件を設定。親側でマスタ情報の取得を先に行う必要がある場合、取得完了後に実施
      if (this.isParentLoaded) {
        common.recoverSearchConditions(this.saveKey, this.conditions, `${this.storePath}/searchKeyword`, `${this.storePath}/isAndSearch`, `${this.storePath}/maxGetCount`)
      }
    }
  },
  methods: {
    ...mapActions('LocalStorage', [
      'setSortCondition',
      'setPaginationSize'
    ]),
    setStore (key, val) {
      this.$store.dispatch({
        type: 'set',
        states: { [key]: val }
      })
    },
    search: async function () {
      await this.$refs.searchComponent.search()
    },
    onSearch (keyword, conditions) {
      this.$emit('search', keyword, conditions)
    },
    onSearched (result) {
      if (this.isLimitCheck) {
        const key = _.get(Object.keys(_.get(result, 'data', {})), '0', '')
        const totalCount = _.get(result, `data.${key}.totalCount`, 0)
        const listSize = _.chain(result).get(`data.${key}.edges`).size().value()

        if (totalCount !== listSize && totalCount !== 0) {
          // 検索結果の件数が上限を超えた場合
          this.$notify.warning(message.MSG_305({ limit: listSize, count: totalCount }))
        }
      }

      this.$emit('searched', result)
    },
    dataConvert (result) {
      this.$emit('dataConvert', result)
    },
    onRowClick (row, column, event) {
      this.$emit('row-click', row, column, event)
    },
    onSortChange ({ column, prop, order }) {
      this.setSortCondition({
        path: this.name,
        sort: { prop, order, sortBy: (!column || !column.sortBy) ? null : column.sortBy }
      })
      this.$emit('sort-change', { column, prop, order })
    },
    onPaginationCurrentChange (val) {
      this.localCurrentPage = val
      if (this._saveCurrentPage) {
        this.setStore(`${this.storePath}/${this.currentPageKey}`, val)
      }
      this.$emit('pagination-current-change', val)
    },
    onPaginationSizeChange (val) {
      this.setStore(`${this.storePath}/${this.sizeKey}`, val)
      this.setPaginationSize({ path: this.name, size: val })

      this.$emit('pagination-size-change', val)
    }
  }
}
</script>
